import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowUp, ChevronUp, Image as ImageIcon, ListPlus, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import type { RunMessageOutcome, RunQueuedMessage } from '@shared/types/factory';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/LoadError';
import { Textarea } from '@/components/ui/textarea';
import { useLoader } from '@/hooks/use-loader';
import { useIsLargeScreen } from '@/hooks/use-large-screen';
import type { ApiResponse } from '@/lib/api-contract';
import { describeApiError } from '@/lib/api-errors';
import { editQueuedMessage, listQueuedMessages, removeQueuedMessage, sendQueuedNow } from '@/lib/runs-api';
import { cn } from '@/lib/utils';
import { messageOutcomeText } from './live-ui';

/** Read again this often while the run is going (each turn takes what's queued). */
const QUEUE_POLL_MS = 5000;
/** Shown before "Show all N queued" (t3code's 3). */
const SHOWN = 3;

/** What the list does with a message: the hub's by default; the gallery passes stand-ins. */
export interface QueuedMessagesApi {
  list: typeof listQueuedMessages;
  edit: typeof editQueuedMessage;
  remove: typeof removeQueuedMessage;
  sendNow: typeof sendQueuedNow;
}

const HUB: QueuedMessagesApi = { list: listQueuedMessages, edit: editQueuedMessage, remove: removeQueuedMessage, sendNow: sendQueuedNow };

/**
 * SF-T-266: the messages a live run holds for its agent's next turn, over the Agent tab's box, as t3code shows its
 * queue: dashed bubbles on the right, each with Send now (steered into the turn that's running), Edit and Remove.
 * Read every few seconds while the run goes; `refreshKey` reads again at once (a message was just queued).
 */
export function QueuedMessages({
  runId,
  project,
  polling,
  canSteer,
  refreshKey = 0,
  onSent,
  api = HUB,
  onCount,
  variant = 'auto',
}: {
  runId: string;
  project?: string;
  /** The run is going: read again every few seconds. */
  polling: boolean;
  /** A turn is running: Send now can steer into it. */
  canSteer: boolean;
  refreshKey?: number;
  onSent?: (outcome: RunMessageOutcome) => void;
  api?: QueuedMessagesApi;
  /** How many are queued, after each read (the Working pill says it). */
  onCount?: (count: number) => void;
  /**
   * SF-T-266: how it shows. `list`: the bubbles (large screens). `bar`: one slim bar ("2 queued · the newest") that
   * opens upwards to them, so the queue never hides the thread. `rows`: one line each with Send now, as T3 Code's
   * app's Queued panel (inside the Working pill's popover). `count`: nothing shown, only read for `onCount` (the
   * pill's "N queued"). `auto` (the default): the list from 1024px, else the bar.
   */
  variant?: 'auto' | 'list' | 'bar' | 'rows' | 'count';
}) {
  const loader = useLoader((signal) => api.list(runId, { project, signal }), [api, runId, project, refreshKey], {
    // One read for the run, however many places show its queue (the bar, the Working pill's panel).
    key: `run-queue:${runId}:${refreshKey}`,
    pollMs: polling ? QUEUE_POLL_MS : undefined,
    pollWhile: () => polling,
  });
  const [all, setAll] = useState(false);
  const large = useIsLargeScreen();
  const shape = variant === 'auto' || variant === 'count' ? (large ? 'list' : 'bar') : variant;
  const compact = shape !== 'list';
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const messages = loader.data?.data?.messages ?? [];
  // The count, told to the Working pill when it changes.
  const onCountRef = useRef(onCount);
  useLayoutEffect(() => {
    onCountRef.current = onCount;
  });
  useEffect(() => {
    onCountRef.current?.(messages.length);
  }, [messages.length]);
  // A read that failed says so (with Retry), rather than looking like an empty queue (SF-T-266 review).
  const readError = loader.data && !loader.data.data ? describeApiError(loader.data) : null;
  if (variant === 'count') return null;
  if (readError && !messages.length) {
    return <LoadError compact title="Couldn't read the queued messages" error={readError} onRetry={loader.reload} />;
  }
  if (!messages.length) return null;
  const shown = all || compact ? messages : messages.slice(-SHOWN);

  const after = (res: ApiResponse<{ messages: RunQueuedMessage[] }>, failed: string) => {
    if (!res.data) {
      toast.error(describeApiError(res, failed));
      loader.reload();
      return false;
    }
    loader.setData(res);
    return true;
  };
  const save = async (id: string, text: string) => {
    setBusy(id);
    if (after(await api.edit(runId, id, text.trim(), { project }), "Couldn't change the message")) setEditing(null);
    setBusy(null);
  };
  const remove = async (id: string) => {
    setBusy(id);
    after(await api.remove(runId, id, { project }), "Couldn't remove the message");
    setBusy(null);
  };
  const sendNow = async (id: string) => {
    setBusy(id);
    const res = await api.sendNow(runId, id, { project });
    setBusy(null);
    if (!res.data) {
      toast.error(describeApiError(res, "Couldn't send it now"));
      return;
    }
    toast.success(messageOutcomeText(res.data));
    loader.reload();
    onSent?.(res.data);
  };

  const list = (
    <ol className="space-y-2" aria-label="Queued for the agent's next turn">
      {messages.length > shown.length ? (
        <li className="text-right">
          <Button type="button" variant="link" size="xs" className="h-auto px-0 text-2xs" onClick={() => setAll(true)}>
            Show all {messages.length} queued
          </Button>
        </li>
      ) : null}
      {shown.map((m) => (
        <li key={m.id}>
          <QueuedBubble
            body={m.body}
            pictures={m.pictures}
            label={m.sending ? 'Going to the agent…' : `Queued by ${m.author}`}
            locked={m.sending}
            busy={busy === m.id}
            editing={editing?.id === m.id}
            onEditing={(on) => setEditing(on ? { id: m.id, text: m.body } : null)}
            onSave={(text) => save(m.id, text)}
            onRemove={() => remove(m.id)}
            sendNow={{
              disabled: !!m.pictures || !canSteer,
              hint: m.sending
                ? 'Going to the agent now'
                : m.pictures
                  ? "Pictures can't go into a turn that's running: this goes with the next one"
                  : canSteer
                    ? 'Steer it into the turn the agent is working on now'
                    : "The agent isn't working on a turn now: this goes as its next one",
              onClick: () => void sendNow(m.id),
            }}
          />
        </li>
      ))}
    </ol>
  );
  if (shape === 'list') return list;
  if (shape === 'rows') {
    // As T3 Code's app's Queued panel: one line each, Send now on the right; Edit opens it in place.
    return (
      <ol className="divide-y" aria-label="Queued for the agent's next turn">
        {messages.map((m) => (
          <li key={m.id} className="py-2">
            {editing?.id === m.id ? (
              <QueuedBubble
                body={m.body}
                label=""
                busy={busy === m.id}
                editing
                onEditing={(on) => setEditing(on ? { id: m.id, text: m.body } : null)}
                onSave={(text) => save(m.id, text)}
                onRemove={() => remove(m.id)}
              />
            ) : (
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="min-w-0 flex-1 truncate text-sm">{m.body}</span>
                <Button
                  type="button"
                  size="xs"
                  className="shrink-0 rounded-full px-3 pointer-coarse:h-9"
                  disabled={m.sending || !!m.pictures || !canSteer || busy === m.id}
                  disabledReason={m.sending ? 'Going to the agent now' : m.pictures ? "Pictures can't go into a turn that's running" : canSteer ? null : "The agent isn't working on a turn now"}
                  loading={busy === m.id}
                  onClick={() => void sendNow(m.id)}
                >
                  <ArrowUp />
                  Send now
                </Button>
                <Button type="button" variant="ghost" size="icon-xs" className="size-8 shrink-0" disabled={m.sending} aria-label="Edit the queued message" onClick={() => setEditing({ id: m.id, text: m.body })}>
                  <Pencil />
                </Button>
                <Button type="button" variant="ghost" size="icon-xs" className="size-8 shrink-0" disabled={m.sending} aria-label="Remove the queued message" onClick={() => void remove(m.id)}>
                  <X />
                </Button>
              </div>
            )}
          </li>
        ))}
      </ol>
    );
  }
  const newest = messages[messages.length - 1]!;
  return (
    <div className="overflow-hidden rounded-xl border border-dashed border-muted-foreground/40 bg-background">
      {open ? <div className="max-h-[45dvh] overflow-y-auto border-b border-dashed border-muted-foreground/40 p-2">{list}</div> : null}
      <button
        type="button"
        className="flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:text-foreground pointer-coarse:min-h-11"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <ListPlus className="size-3.5 shrink-0" aria-hidden />
        <span className="shrink-0 font-medium text-foreground">{messages.length} queued</span>
        {open ? null : <span className="min-w-0 truncate">· {newest.body}</span>}
        <ChevronUp className={cn('ml-auto size-4 shrink-0 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
    </div>
  );
}

/**
 * SF-T-266: one message waiting for its turn, as t3code shows it: a dashed bubble on the right with what it says
 * (and how many pictures), and under it who queued it, Send now (when given), Edit (in place) and Remove. The Agent
 * tab's queue and Chat's messages waiting for their turn.
 */
export function QueuedBubble({
  body,
  pictures = 0,
  label,
  locked = false,
  busy = false,
  editing,
  onEditing,
  onSave,
  onRemove,
  sendNow,
}: {
  body: string;
  pictures?: number;
  /** Under it: "Queued by Deon", "Waiting for its turn". */
  label: string;
  /** Going to the agent now: can't be changed. */
  locked?: boolean;
  busy?: boolean;
  editing: boolean;
  onEditing: (on: boolean) => void;
  onSave: (text: string) => void | Promise<void>;
  onRemove: () => void | Promise<void>;
  /** Send now, when the message can be steered in (the Agent tab). */
  sendNow?: { disabled: boolean; hint: string; onClick: () => void } | null;
}) {
  const [text, setText] = useState(body);
  const off = locked || busy;
  return (
    <div className="flex flex-col items-end gap-0.5">
      <div
        className={cn(
          'max-w-[min(40rem,85%)] min-w-0 rounded-2xl border border-dashed px-3.5 py-2',
          editing ? 'w-full border-primary/60' : 'border-muted-foreground/40'
        )}
      >
        {editing ? (
          <div className="space-y-2">
            <Textarea
              aria-label="The queued message"
              autoFocus
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim()) {
                  e.preventDefault();
                  void onSave(text);
                }
                if (e.key === 'Escape') onEditing(false);
              }}
            />
            <div className="flex justify-end gap-1">
              <Button type="button" variant="ghost" size="xs" onClick={() => onEditing(false)}>
                Cancel
              </Button>
              <Button type="button" size="xs" disabled={!text.trim()} loading={busy} onClick={() => void onSave(text)}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="line-clamp-4 text-sm break-words whitespace-pre-wrap text-muted-foreground">{body}</p>
            {pictures ? (
              <p className="mt-1 flex items-center gap-1 text-2xs text-muted-foreground">
                <ImageIcon className="size-3" aria-hidden />
                {pictures === 1 ? '1 picture' : `${pictures} pictures`}
              </p>
            ) : null}
          </>
        )}
      </div>
      {editing ? null : (
        <div className="flex items-center gap-0.5 text-2xs text-muted-foreground">
          <span className="mr-1">{label}</span>
          {sendNow ? (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="h-6 px-1.5 text-2xs"
              disabled={off || sendNow.disabled}
              disabledReason={sendNow.hint}
              loading={busy}
              onClick={sendNow.onClick}
              data-hint={sendNow.hint}
            >
              <ArrowUp />
              Send now
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="size-6"
            disabled={off}
            aria-label="Edit the queued message"
            onClick={() => {
              setText(body);
              onEditing(true);
            }}
          >
            <Pencil />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="size-6"
            disabled={off}
            aria-label="Remove the queued message"
            onClick={() => void onRemove()}
          >
            <X />
          </Button>
        </div>
      )}
    </div>
  );
}
